import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { conversationQueryFrom, fetchConversation, listConversations } from '@/api/conversations';
import { queryKeys } from '@/lib/query-keys';
import type { ConversationFiltros } from '@/types/conversations';
import { INBOX_REFRESH_MS } from './inbox-format';

/**
 * Bandeja del inbox del negocio activo (módulo 5).
 *
 * El listado lo lee cualquier miembro del tenant (el AGENTE_HUMANO atiende la bandeja); lo que
 * cambia por rol es el handoff, que es solo ADMIN (ver `useSetBotActive`).
 *
 * Se refresca solo cada `INBOX_REFRESH_MS` porque el inbox no lo mueve el usuario: llegan mensajes
 * de los canales y la conversación sube en el orden por actividad. Es un `refetchInterval` (no
 * websockets: el backend no los expone) y con `keepPreviousData` la página no queda en blanco al
 * cambiar de filtro o de página.
 */

/** `GET /api/tenants/:tenantId/conversations` — página de la bandeja (filtros + `limite`/`offset`). */
export function useConversations(tenantId: string | null, filtros: ConversationFiltros = {}) {
  // Los mismos filtros que viajan por la URL forman la query key: así la caché no mezcla páginas
  // ni combinaciones de filtros, y `conversationQueryFrom` omite lo que no va.
  const query = conversationQueryFrom(filtros);

  return useQuery({
    queryKey: queryKeys.conversations.list(tenantId ?? '', query),
    queryFn: () => listConversations(tenantId ?? '', filtros),
    enabled: Boolean(tenantId),
    refetchInterval: INBOX_REFRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/tenants/:tenantId/conversations/:conversationId` — tarjeta + últimas órdenes.
 *
 * Sirve para el panel de la derecha: el listado ya trae la tarjeta, pero el detalle añade las
 * últimas 5 órdenes del contacto. Con `conversationId` vacío la query no corre (así el panel
 * puede montarse sin conversación elegida y sin disparar una petición).
 */
export function useConversation(tenantId: string | null, conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.conversations.detail(tenantId ?? '', conversationId ?? ''),
    queryFn: () => fetchConversation(tenantId ?? '', conversationId ?? ''),
    enabled: Boolean(tenantId && conversationId),
    refetchInterval: INBOX_REFRESH_MS,
  });
}
